// §10.6: three-mode confirm modal — Mark won, Mark lost, Reopen.
// All three append immutable events; the body always says so.
//
// Composed from `frontend/src/ui/` primitives (Button + Textarea +
// Select). We don't reuse <ConfirmReasonModal> directly because
// Mark-lost needs a categorical lossKind dropdown above the reason.

import React, { useEffect, useState } from 'react';
import { Button, Select, Textarea } from '../../../ui';
import { LOSS_REASONS, LossReason } from '../../../types/pipeline';

export type DealActionKind = 'won' | 'lost' | 'reopen';

interface Props {
  pending: DealActionKind | null;
  cycleAfterReopen: number;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (payload: {
    kind: DealActionKind;
    reason: string;
    lossKind: LossReason | null;
  }) => void;
}

const DealConfirmModal: React.FC<Props> = ({
  pending,
  cycleAfterReopen,
  saving = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [lossKind, setLossKind] = useState<LossReason>(LOSS_REASONS[0]);

  useEffect(() => {
    if (pending) {
      setReason('');
      setLossKind(LOSS_REASONS[0]);
    }
  }, [pending]);

  if (!pending) return null;

  const title =
    pending === 'won'
      ? 'Mark this deal won?'
      : pending === 'lost'
        ? 'Mark this deal lost?'
        : `Reopen this deal · cycle ${cycleAfterReopen}?`;

  const placeholder =
    pending === 'reopen' ? 'What changed?' : 'Context for cohort analysis…';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !saving && onCancel()}
    >
      <div
        className="w-full max-w-md bg-surface-1 border border-white/[0.16] rounded-ds-xl p-5 shadow-ds-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base font-semibold text-slate-50 mb-1.5">
          {title}
        </div>
        <div className="text-sm text-slate-400 mb-3.5 leading-relaxed">
          This appends to the immutable terminal history. It cannot be edited
          or deleted — only superseded by another event.
        </div>

        {pending === 'lost' && (
          <Select
            label="Loss kind (categorical)"
            value={lossKind}
            onChange={(e) => setLossKind(e.target.value as LossReason)}
            disabled={saving}
            className="mb-3"
          >
            {LOSS_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        )}

        <Textarea
          label={pending === 'reopen' ? 'Reopen reason' : 'Reason'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder}
          rows={3}
          disabled={saving}
        />

        <div className="flex justify-end gap-2 mt-3.5">
          <Button intent="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            intent={pending === 'lost' ? 'danger' : 'primary'}
            size="sm"
            disabled={saving}
            onClick={() =>
              onConfirm({
                kind: pending,
                reason: reason.trim(),
                lossKind: pending === 'lost' ? lossKind : null,
              })
            }
          >
            {saving ? 'Saving…' : 'Append event'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DealConfirmModal;
